import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import "chart.js/auto";

// Dummy IoT data generator
const generateIoTData = () => ({
  temperature: (20 + Math.random() * 10).toFixed(2),
  humidity: (40 + Math.random() * 20).toFixed(2),
  pressure: (1010 + Math.random() * 10).toFixed(2),
  status: Math.random() > 0.9 ? "ALERT" : "NORMAL",
  timestamp: new Date(),
});

// Dummy cameras setup
const initialCameras = [
  { id: 1, name: "Camera A", lat: 28.6139, long: 77.209 },
  { id: 2, name: "Camera B", lat: 19.076, long: 72.8777 },
  { id: 3, name: "Camera C", lat: 12.9716, long: 77.5946 },
  { id: 4, name: "Camera D", lat: 13.0827, long: 80.2707 },
];

// Dummy image gallery
const initialImages = [
  {
    url: "https://via.placeholder.com/400x250.png?text=Captured+Image+1",
    lat: 28.6139,
    long: 77.209,
    timestamp: new Date(),
  },
  {
    url: "https://via.placeholder.com/400x250.png?text=Captured+Image+2",
    lat: 19.076,
    long: 72.8777,
    timestamp: new Date(),
  },
];

const TwinGuardHome = () => {
  const [cameraFeeds, setCameraFeeds] = useState(
    initialCameras.map((cam) => ({ ...cam, iotHistory: [generateIoTData()], streaming: false }))
  );
  const [images, setImages] = useState(initialImages);

  // Simulate IoT feed per camera
  useEffect(() => {
    const interval = setInterval(() => {
      setCameraFeeds((prev) =>
        prev.map((cam) => {
          const newIot = generateIoTData();
          return {
            ...cam,
            iotHistory: [...cam.iotHistory.slice(-19), newIot],
          };
        })
      );
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Capture new image
  const captureNewImage = (cameraId) => {
    const camera = cameraFeeds.find((c) => c.id === cameraId);
    const newImage = {
      cameraId: camera.id,
      url: `https://via.placeholder.com/400x250.png?text=Captured+Image+${images.length + 1}`,
      lat: camera.lat + Math.random() * 0.01,
      long: camera.long + Math.random() * 0.01,
      timestamp: new Date(),
    };
    setImages([newImage, ...images]);
  };

  // Toggle streaming
  const toggleStreaming = (cameraId) => {
    setCameraFeeds((prev) =>
      prev.map((cam) =>
        cam.id === cameraId ? { ...cam, streaming: !cam.streaming } : cam
      )
    );
  };

  // Helper: chart config per camera
  const getChartData = (iotHistory) => ({
    labels: iotHistory.map((d) => d.timestamp.toLocaleTimeString("en-US", { hour12: false })),
    datasets: [
      {
        label: "Temperature (°C)",
        data: iotHistory.map((d) => d.temperature),
        borderColor: "rgba(255,99,132,1)",
        backgroundColor: "rgba(255,99,132,0.2)",
        tension: 0.3,
      },
      {
        label: "Humidity (%)",
        data: iotHistory.map((d) => d.humidity),
        borderColor: "rgba(54,162,235,1)",
        backgroundColor: "rgba(54,162,235,0.2)",
        tension: 0.3,
      },
      {
        label: "Pressure (hPa)",
        data: iotHistory.map((d) => d.pressure),
        borderColor: "rgba(255,206,86,1)",
        backgroundColor: "rgba(255,206,86,0.2)",
        tension: 0.3,
      },
    ],
  });

  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: "top", labels: { color: "#f0f0f0" } } },
    animation: { duration: 500 },
    scales: {
      y: { beginAtZero: false, ticks: { color: "#f0f0f0" } },
      x: { ticks: { color: "#f0f0f0" } },
    },
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white px-4 md:px-12 py-4">
      {/* Header */}
      <header className="text-center mb-12">
        <h1 className="text-xl md:text-2xl font-bold text-yellow-200 mb-2">TwinGuard</h1>
        <p className="text-m md:text-lg mx-auto">
          Preventing Tragedies Before They Happen: TwinGuard Brings Real-Time Safety Monitoring to Pharma, Food and Hospitality
        </p>
        <p className="text-m md:text-lg text-yellow-200 mx-auto">
            Serious Risks, Smarter Solutions: TwinGuard Ensures Safety Across Industries
        </p>
      </header>

      {/* About Section */}
      <section className="w-full bg-gray-800 shadow-lg rounded-lg p-6 mb-2">
        <h2 className="text-lg font-semibold mb-4">About TwinGuard</h2>
        <p className="text=sm">
          TwinGuard provides real-time monitoring, predictive alerts, and preventive quality assurance across pharma, food, and hospitality. Integrating IoT metrics, live video, and image capture, it ensures safety and compliance before incidents occur.
        </p>
      </section>

      {/* Multi-Camera Feeds */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
        {cameraFeeds.map((cam) => {
          const latest = cam.iotHistory[cam.iotHistory.length - 1];
          return (
            <div key={cam.id} className="bg-gray-800 shadow-lg rounded-lg p-4 flex flex-col">
              <div className="relative w-full h-48 md:h-64 bg-black rounded-lg flex items-center justify-center text-white text-xl mb-2">
                {cam.streaming ? `${cam.name} Live...` : `${cam.name} Placeholder`}
              </div>
              <div className="flex justify-between mb-2 gap-4">
                <button
                  onClick={() => toggleStreaming(cam.id)}
                  className={`px-3 py-1 rounded-lg text-white ${
                    cam.streaming ? "bg-red-600" : "bg-blue-600"
                  }`}
                >
                  {cam.streaming ? "Stop Streaming" : "Start Streaming"}
                </button>
                <button
                  onClick={() => captureNewImage(cam.id)}
                  className="px-3 py-1 rounded-lg bg-green-600 text-white"
                >
                  Capture Image
                </button>
              </div>
              {/* IoT Metrics */}
              <div className="grid grid-cols-2 gap-2 bg-gray-700 p-2 rounded-lg text-sm mb-2">
                <div>
                  <strong>Temp:</strong> {latest.temperature} °C
                </div>
                <div>
                  <strong>Humidity:</strong> {latest.humidity} %
                </div>
                <div>
                  <strong>Pressure:</strong> {latest.pressure} hPa
                </div>
                <div className={`${latest.status === "ALERT" ? "text-red-400" : ""}`}>
                  <strong>Status:</strong> {latest.status}
                </div>
                <div className="col-span-2 text-xs text-gray-400">
                  Feed by Think5Whys from Lat: {cam.lat.toFixed(4)} Long: {cam.long.toFixed(4)} | Date: {latest.timestamp.toISOString()}
                </div>
              </div>

              {/* IoT Chart */}
              <div className="bg-gray-900 p-2 rounded-lg">
                <Line data={getChartData(cam.iotHistory)} options={chartOptions} />
              </div>
            </div>
          );
        })}
      </section>

      {/* Captured Image Gallery */}
      <section className="w-full bg-gray-800 shadow-lg rounded-lg p-6 mb-2">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl text-yellow-200 font-semibold">Captured Image Gallery</h2>
          <button
            onClick={() => captureNewImage()}
            className="bg-green-600 text-white px-4 py-2 rounded-lg w-60"
          >
            Capture New Image
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {images.map((img, idx) => (
            <div key={idx} className="rounded-lg overflow-hidden shadow-md">
              <img
                src={img.url}
                alt={`Captured ${idx + 1}`}
                className="w-full h-48 object-cover"
              />
              <div className="p-2 bg-gray-900">
                <p className="text-gray-200 text-sm">
                  Camera ID: {img.cameraId || 0} | Lat: {img.lat.toFixed(4)} Long: {img.long.toFixed(4)}
                </p>
                <p className="text-gray-400 text-xs">
                  Date: {img.timestamp.toISOString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full text-center mt-12 text-gray-400 text-sm">
        &copy; {new Date().getFullYear()} Think5Whys | TwinGuard Module
      </footer>
    </div>
  );
};

export default TwinGuardHome;
